(ns app.front.view.inspections.recurrence
  "Frontend elements for creating recurring (repeating) inspections at the end of an inspection visit"
  (:require [app.domain.blackout.model :as blackout-model]
            [app.domain.inspection.model :as inspection-model]
            [app.front.ui.yes-no-radio-buttons :as yes-no]
            [app.front.view.inspections.booking-dialog :as booking-dialog]
            [core.db.child-entity :as child]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]))


(defn get-recurrence-params
  [inspection {:keys [date-time-slots] :as booking-data}]
  (let [{:keys [date time-slot-id]} (first date-time-slots)]
    (when date
      (p/assoc-if {:time-slot-id time-slot-id
                   :year         (.getFullYear date)
                   :month        (inc (.getMonth date)) ; JavaScript month is [0...11]
                   :day          (.getDate date)
                   :recurring?   true
                   :interval     (:inspection/recurrence-interval inspection)}
                  :assignee (:booking/assignee booking-data)
                  :comment (:booking/notes booking-data)))))


(defn add-to-visit-end-signal-params
  "Adds the parameters required for creating a new inspection and booking for a recurring inspection"
  [inspection recurrence-values params]
  (let [booking-data (<sub [::booking-dialog/booking-data])]
    (if (:create-inspection? recurrence-values)
      (assoc params :recurrence (get-recurrence-params inspection booking-data))
      params)))

(defn next-working-day
  "Returns the next working day after the given date (inclusive),
  skipping blackouts and non-standard workdays for the given auth-group."
  [date auth-group]
  (let [b-days   (blackout-model/current-group-blackouts auth-group (date-util/add-days-to-date date -1))
        weekday? (some? (inspection-model/standard-workdays (.getDay date)))
        b-day    (some #(blackout-model/applies-to-date? (date-util/date-at-time-in-tz date 0 0 (:group/tz auth-group)) %)
                       b-days)]
    (if (and weekday? (not b-day))
      date
      (recur (date-util/add-days-to-date date 1) auth-group))))

(defn view
  "If a visit for a recurring (e.g. annual) inspection is being marked as passed,
  the user is asked if they want to recur the inspection, i.e. creating a request for the next visit.
  If they do, a new inspection and an inspection request is created for them when they close the inspection visit.

  To be clear, there are two inspections and bookings to consider here:
  1. those related to the current visit
  2. those to be created for the next visit some time in the future"
  [{:keys [tr inspection visit-state values on-change class in-modal?]
    :or   {in-modal? false}}]
  (when (:inspection/recurring? inspection)
    (let [create-insp?          (-> values :create-inspection? (not= false))
          auth-group            (inspection-model/authority-group inspection)
          booking               (<sub [::booking-dialog/booking-data])
          date-time-slot        (<sub [::booking-dialog/booking-date-time-slot])
          date-chosen?          (and (:date date-time-slot)
                                     (:time-slot-id date-time-slot))
          old-interval          (:inspection/recurrence-interval inspection)
          default-date          (-> (date-util/add-months-to-date (js/Date.) (or old-interval 0))
                                    (next-working-day auth-group))
          time-slot-id          (->> (<sub [::booking-dialog/request-time-slots auth-group default-date])
                                     (filter #(= (:time-slot/weekday %) (.getDay default-date)))
                                     first
                                     :db/id)
          set-default-date-time (fn [create?]
                                  (on-change create?)
                                  (when create?
                                    (>evt [::booking-dialog/new-recurring-inspection-booking default-date])
                                    (>evt [::booking-dialog/set-booking-date-time-slot-data 0 :date default-date])
                                    (>evt [::booking-dialog/set-booking-date-time-slot-data 0 :time-slot-id time-slot-id])))]

      (when (and create-insp? (not date-time-slot))
        (set-default-date-time true)
        (>evt [::booking-dialog/set-booking-data :booking/assignee
               (some-> ((child/child-entity-of-type-fn :entity.type/booking) inspection)
                       :booking/assignee
                       :db/id)]))
      (when (and (or (inspection-model/passed-or-completed-result? visit-state)
                     (= :inspection.visit.result/passed visit-state))
                 (-> auth-group :group/features :group.feature/recurring-inspections))
        [:div.mt-10
         {:class class}
         [yes-no/yes-no-radio-buttons
          tr
          "inspection-result-recurring"
          {:tr-yes         :inspection.visit/recur-yes
           :tr-no          :inspection.visit/recur-no
           :class-name     "mb-4"
           :value-selected create-insp?
           :on-change      set-default-date-time}]
         (when create-insp?
           [:<>
            [booking-dialog/render-date-picker (util/mmap {:fixed? in-modal?}
                                                          tr auth-group)]
            (when date-chosen?
              [booking-dialog/render-comment-field tr booking])])]))))
